/**
 * Confirmation Modal for Source Registration
 * Shows file list, file tree, and estimated processing time before registration
 */

import React from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { File, Clock, X } from 'lucide-react';
import { FileTreeNode } from './FileTreeNode';

interface ConfirmRegistrationModalProps {
  uploadInfo: {
    files: Array<{ name: string; size: number; extension: string; path: string; rows?: number; columns?: number }>;
    totalSize: number;
    byExtension: Record<string, { count: number; totalSize: number }>;
    filteredTree: any[];
  };
  duration: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  confirmModalTab: 'list' | 'tree';
  setConfirmModalTab: (tab: 'list' | 'tree') => void;
  modalExpandedPaths: Set<string>;
  setModalExpandedPaths: React.Dispatch<React.SetStateAction<Set<string>>>;
  formatFileSize: (bytes: number) => string;
  supportedExtensions: Set<string>;
  extensionDescriptions: Record<string, string>;
  selectedExtensions: string[];
}

export function ConfirmRegistrationModal({
  uploadInfo,
  duration,
  onConfirm,
  onCancel,
  loading,
  confirmModalTab,
  setConfirmModalTab,
  modalExpandedPaths,
  setModalExpandedPaths,
  formatFileSize,
  supportedExtensions,
  extensionDescriptions,
  selectedExtensions,
}: ConfirmRegistrationModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Confirm Source Registration</h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>Summary:</strong> {uploadInfo.files.length} file{uploadInfo.files.length !== 1 ? 's' : ''} 
              {' '}({formatFileSize(uploadInfo.totalSize)}) will be uploaded and processed as a background job.
            </p>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b">
            <button
              onClick={() => setConfirmModalTab('list')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                confirmModalTab === 'list'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              File List ({uploadInfo.files.length})
            </button>
            <button
              onClick={() => setConfirmModalTab('tree')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                confirmModalTab === 'tree'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              File Tree
            </button>
          </div>
          
          {/* File List Tab */}
          {confirmModalTab === 'list' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Files to be uploaded:</h4>
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                <div className="divide-y divide-gray-200">
                  {uploadInfo.files.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <File className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate" title={file.path}>
                          {file.name}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">{file.extension}</span>
                        {file.rows !== undefined && file.columns !== undefined && (
                          <span className="text-xs text-blue-600 flex-shrink-0">
                            ({file.rows} rows × {file.columns} cols)
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-600 flex-shrink-0 ml-2">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* File type breakdown */}
              {Object.keys(uploadInfo.byExtension).length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h5 className="text-sm font-semibold text-gray-700 mb-2">Files by Type:</h5>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {Object.entries(uploadInfo.byExtension).map(([ext, data]) => (
                      <div key={ext} className="p-2 bg-gray-50 rounded text-sm">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{ext}</div>
                        <div className="text-xs text-gray-600">
                          {data.count} file{data.count !== 1 ? 's' : ''}, {formatFileSize(data.totalSize)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* File Tree Tab */}
          {confirmModalTab === 'tree' && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">File locations in folder structure:</h4>
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto p-3 bg-gray-50">
                {uploadInfo.filteredTree.length > 0 ? (
                  uploadInfo.filteredTree.map((node: any, idx: number) => (
                    <FileTreeNode
                      key={idx}
                      node={node}
                      level={0}
                      toggleHidePath={() => {}}
                      hiddenPaths={new Set()}
                      formatFileSize={formatFileSize}
                      supportedExtensions={supportedExtensions}
                      extensionDescriptions={extensionDescriptions}
                      selectedExtensions={selectedExtensions}
                      expandedTreePaths={modalExpandedPaths}
                      setExpandedTreePaths={setModalExpandedPaths}
                    />
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No files selected</p>
                )}
              </div>
            </div>
          )}
          
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">Estimated Processing Time:</p>
              <p className="text-sm text-yellow-800">{duration}</p>
            </div>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onCancel} className="flex-1" disabled={loading}>
              Cancel
            </Button>
            <Button onClick={onConfirm} className="flex-1" disabled={loading}>
              {loading ? 'Registering...' : 'Confirm & Register'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}













